import "./ui.css";

export type SegmentOption<T extends string> = {
  value: T;
  label: string;
  /**
   * Optional numeric badge rendered inside the tab label. Omit when not
   * relevant. Renders as a small pill (gray in pill variant, dark ink in
   * underline variant); the on-state picks up the variant's accent colors.
   */
  count?: number;
};

/**
 * Single-select tab control. Default visual is a pill-style toggle for 2–3
 * mutually-exclusive view modes (the prototype's `ViewSegmented` /
 * `PlanViewToggle` / `SelectPills`); `variant="underline"` switches to a
 * flat row of text tabs with an underline beneath the active option, for
 * surfaces with more vertical hierarchy (e.g. a modal's right pane).
 *
 * Both variants share identical tablist a11y semantics — the visual delta
 * is pure CSS scoped under `.fgx-seg--underline`. Adding a third variant
 * should follow the same pattern.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  fill = false,
  variant = "pill",
  ariaLabel,
  ariaLabelledby
}: {
  options: ReadonlyArray<SegmentOption<T>>;
  value: T;
  onChange: (next: T) => void;
  /** Stretch segments to fill the container width. */
  fill?: boolean;
  /**
   * Visual variant. `"pill"` (default) is the enclosed gray-track style;
   * `"outline"` is a white pill with a hairline border and a subtle gray
   * active fill (the Fueling Plan view toggle / plan-history filter look);
   * `"underline"` is a flat row with an active underline. Keep `"pill"` for
   * dense toggle rows (2–3 short options) and prefer `"underline"` when the
   * tabs sit at the top of a content panel.
   */
  variant?: "pill" | "outline" | "underline";
  /** Visible caption id — use when the control sits beside/on a labelled row. */
  ariaLabelledby?: string;
  /** Fallback accessible name when `aria-labelledby` is not set. */
  ariaLabel?: string;
}) {
  const tablistA11y =
    ariaLabelledby != null && ariaLabelledby !== ""
      ? { "aria-labelledby": ariaLabelledby }
      : ariaLabel != null && ariaLabel !== ""
        ? { "aria-label": ariaLabel }
        : {};

  const classes = [
    "fgx-seg",
    variant === "underline" ? "fgx-seg--underline" : null,
    variant === "outline" ? "fgx-seg--outline" : null,
    fill ? "fgx-seg--fill" : null
  ]
    .filter((c): c is string => c != null)
    .join(" ");

  return (
    <div className={classes} role="tablist" {...tablistA11y}>
      {options.map((opt) => {
        const on = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={on}
            className={`fgx-seg__btn${on ? " is-on" : ""}`}
            onClick={() => onChange(opt.value)}>
            <span>{opt.label}</span>
            {opt.count != null && (
              <span className="fgx-seg__count" aria-hidden>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
